import { ECONTEST_PHASE, IContest, IContestPrizePool } from '../../types/global/helper';
import { IAwardCategory } from '../../redux/contest/types';

/**
 * Campaign has not gone live yet — entries are not accepted, so apply becomes a notify-me flow.
 * Backend flags drafts with `incomplete`; published-but-not-started campaigns sit in `preparation`.
 */
export const isContestAwaitingLaunch = (contest?: IContest): boolean => {
    if (!contest) return false;
    return Boolean(contest.incomplete) || contest.contest_phase === ECONTEST_PHASE.PREPARATIION;
};

/** Parse any CSS color (rgba, rgb, hex) and return rgba with the given alpha. */
export const withAlpha = (color: string, alpha: number): string => {
    const rgbaMatch = color.match(/rgba?\(\s*([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*[\d.]+)?\s*\)/);
    if (rgbaMatch) {
        return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
    }
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
};

const prizePoolBareAmount = (pool: IContestPrizePool | null | undefined): number | null => {
    if (!pool || pool.bare_amount == null || Number.isNaN(Number(pool.bare_amount))) {
        return null;
    }
    return Number(pool.bare_amount);
};

const hasNonEmptyAdditionalPrizes = (extras?: string[] | null): boolean =>
    Boolean(extras?.some(p => typeof p === 'string' && p.trim().length > 0));

const payoutHasAdditionalPrize = (payout: IAwardCategory | string | null | undefined): boolean => {
    if (!payout || typeof payout === 'string') return false;
    return hasNonEmptyAdditionalPrizes(payout.additional_prize);
};

/** True when any prize / award / tier payout lists non-empty additional (gifted) prizes. */
export const contestHasAdditionalPrizes = (contest: IContest | null | undefined): boolean => {
    if (!contest) return false;

    if (contest.prizes?.some(p => hasNonEmptyAdditionalPrizes((p as IAwardCategory).additional_prize))) {
        return true;
    }

    for (const cat of contest.categories || []) {
        if (cat.category_awards?.some(a => hasNonEmptyAdditionalPrizes(a.additional_prize))) {
            return true;
        }
        for (const placement of cat.prize_structure || []) {
            if ((placement.payouts || []).some(payoutHasAdditionalPrize)) {
                return true;
            }
        }
    }

    return false;
};

const numericPrizeAmount = (payout: { bare_amount?: number | null; prize?: number | string | null }): number => {
    const bare = Number(payout.bare_amount);
    if (!Number.isNaN(bare) && bare > 0) return bare;
    if (typeof payout.prize === 'number' && !Number.isNaN(payout.prize)) return payout.prize;
    if (typeof payout.prize === 'string') {
        const numeric = Number(payout.prize.replace(/[^0-9.-]/g, ''));
        return Number.isNaN(numeric) ? 0 : numeric;
    }
    return 0;
};

/** Fallback when `prize_pool` is missing — sum award amounts from the contest payload. */
const sumAwardsPool = (contest: IContest): number => {
    let total = 0;
    if (contest.prizes && contest.prizes.length > 0) {
        total += contest.prizes.reduce((sum, p) => sum + numericPrizeAmount(p), 0);
    }
    if (contest.categories && contest.categories.length > 0) {
        for (const cat of contest.categories) {
            if (cat.category_awards && cat.category_awards.length > 0) {
                total += cat.category_awards.reduce((sum, a) => sum + numericPrizeAmount(a), 0);
            }
            for (const placement of cat.prize_structure || []) {
                for (const payout of placement.payouts || []) {
                    if (typeof payout === 'string') continue;
                    total += numericPrizeAmount(payout);
                }
            }
        }
    }
    return total;
};

/** Numeric pool amount — prefers `contest.prize_pool.bare_amount`, falls back to summing awards. */
export const calcTotalPool = (contest: IContest | null | undefined): number => {
    if (!contest) return 0;
    const fromApi = prizePoolBareAmount(contest.prize_pool);
    if (fromApi != null) return fromApi;
    return sumAwardsPool(contest);
};

export const formatPool = (amount: number) => {
    return `$${amount.toLocaleString()}`;
};

/**
 * Display string for Starting Reward / Total Pool.
 * Prefers `prize_pool.formatted_for_display`, then builds from bare_amount / currency symbol.
 * When cash pool is $0 but gifted/additional prizes exist, returns "Gifted Prizes".
 */
export const formatContestPool = (contest: IContest | null | undefined): string => {
    if (!contest) return formatPool(0);

    const amount = calcTotalPool(contest);
    if (amount <= 0 && contestHasAdditionalPrizes(contest)) {
        return 'Gifted Prizes';
    }

    const pool = contest.prize_pool;
    const formatted = pool?.formatted_for_display?.trim();
    if (formatted) return formatted;

    const symbol = pool?.currency?.trim();
    if (symbol) {
        return `${symbol}${amount.toLocaleString()}`;
    }
    return formatPool(amount);
};

/** Whether the Starting Reward / Total Pool label should append the "Pool" suffix. */
export const shouldShowPoolSuffix = (contest: IContest | null | undefined): boolean => {
    if (!contest) return true;
    const amount = calcTotalPool(contest);
    return !(amount <= 0 && contestHasAdditionalPrizes(contest));
};

export type ContestSidebarPrizeRow = {
    position: string;
    tierLabel: string;
    cashDisplay: string | null;
    additionalPrizes: string[];
};

export type ContestSidebarPrizeGroup = {
    categoryName?: string;
    rows: ContestSidebarPrizeRow[];
};

const resolveTierLabel = (tierFee: { _id?: string; label?: string }[] | undefined, payout: IAwardCategory): string => {
    const tiers = tierFee || [];
    const match = tiers.find(t => t._id === payout.tier_name || t.label === payout.tier_name);
    return match?.label || payout.tier_name || 'Prize';
};

const cashDisplayForPayout = (payout: {
    bare_amount?: number | null;
    prize?: number | string | null;
    formatted_for_display?: string | null;
    currency?: string | null;
}): string | null => {
    const bare = Number(payout.bare_amount);
    if (!Number.isNaN(bare) && bare > 0) {
        if (payout.formatted_for_display?.trim()) return payout.formatted_for_display.trim();
        const symbol = payout.currency?.trim() || '$';
        return `${symbol}${bare.toLocaleString()}`;
    }

    if (typeof payout.prize === 'number' && payout.prize > 0) {
        if (payout.formatted_for_display?.trim()) return payout.formatted_for_display.trim();
        const symbol = payout.currency?.trim() || '$';
        return `${symbol}${payout.prize.toLocaleString()}`;
    }

    if (typeof payout.prize === 'string') {
        const trimmed = payout.prize.trim();
        if (!trimmed || trimmed === '$0' || /^\$?0(\.0+)?$/.test(trimmed)) return null;
        const numeric = Number(trimmed.replace(/[^0-9.-]/g, ''));
        if (!Number.isNaN(numeric) && numeric > 0) return trimmed;
    }

    return null;
};

/**
 * Flatten category prize_structure (and legacy prizes / category_awards) for the detail sidebar.
 * Includes cash and/or additional gifted prizes, grouped by category when multiple exist.
 */
export const getContestSidebarPrizeGroups = (contest: IContest | null | undefined): ContestSidebarPrizeGroup[] => {
    if (!contest) return [];

    const groups: ContestSidebarPrizeGroup[] = [];
    const categories = contest.categories || [];
    const multiCategory = categories.length > 1;

    for (const cat of categories) {
        const rows: ContestSidebarPrizeRow[] = [];
        const structure = cat.prize_structure || [];

        if (structure.length > 0) {
            for (const placement of structure) {
                for (const raw of placement.payouts || []) {
                    if (typeof raw === 'string') continue;
                    const payout = raw as IAwardCategory;
                    const additionalPrizes = (payout.additional_prize || []).filter(p => p?.trim());
                    const cashDisplay = cashDisplayForPayout(payout);
                    if (!cashDisplay && additionalPrizes.length === 0) continue;
                    rows.push({
                        position: placement.position || payout.tier_position || payout.category_title || 'Prize',
                        tierLabel: resolveTierLabel(cat.tier_fee, payout),
                        cashDisplay,
                        additionalPrizes,
                    });
                }
            }
        } else if (cat.category_awards?.length) {
            for (const award of cat.category_awards) {
                const additionalPrizes = (award.additional_prize || []).filter(p => p?.trim());
                const cashDisplay = cashDisplayForPayout(award as IAwardCategory);
                if (!cashDisplay && additionalPrizes.length === 0) continue;
                rows.push({
                    position: award.category_title || 'Prize',
                    tierLabel: resolveTierLabel(cat.tier_fee, award as IAwardCategory),
                    cashDisplay,
                    additionalPrizes,
                });
            }
        }

        if (rows.length > 0) {
            groups.push({
                categoryName: multiCategory ? cat.category_name : undefined,
                rows,
            });
        }
    }

    if (groups.length === 0 && contest.prizes?.length) {
        const rows: ContestSidebarPrizeRow[] = [];
        for (const prize of contest.prizes) {
            const payout = prize as IAwardCategory;
            const additionalPrizes = (payout.additional_prize || []).filter(p => p?.trim());
            const cashDisplay = cashDisplayForPayout(payout);
            if (!cashDisplay && additionalPrizes.length === 0) continue;
            rows.push({
                position: payout.category_title || 'Prize',
                tierLabel: payout.tier_name || 'Prize',
                cashDisplay,
                additionalPrizes,
            });
        }
        if (rows.length > 0) {
            groups.push({ rows });
        }
    }

    return groups;
};
